import { LitElement, html, css, TemplateResult, PropertyValues, nothing } from "lit";
import { property, customElement } from "lit/decorators.js";
import { AlarmEvents, VesselAlarmsModel } from "../../../src/models/vessel-alarms-model";
import { AlarmsGroupedByDate } from "./types";
import { AlarmState, NotificationResponse, VoyNotification, AlarmTypes, NotificationTypes } from "../../../src/services/types/vessel-notifications-services.types";
import { formatToTitleCase, isObjectEmpty } from "../../../src/utils/helpers";
import { VesselNotificationsService } from "../../../src/services/vessel-notifications-service";
import "@maersk-global/mds-components-core/mc-loading-indicator";

const isVesselOnCloud = (<any>window)["appenv"].vesselOnCloud;
const MINUTE = 60 * 1000;

@customElement("voy-vessel-alarm-log-list")
export class VoyVesselAlarmLogList extends LitElement {
	// alarms sorted and grouped by date
	@property({ type: Array }) _groupedAlarms: AlarmsGroupedByDate = {};
	@property({ attribute: false}) _loader: boolean = false


	constructor() {
		super();

		this._refreshAlarms = this._refreshAlarms.bind(this);
	}

	static get styles() {
		return [
			css`
				:host {
					display: flex;
					flex-direction: column;
					max-height: calc(100vh - 180px);
					width: 324px;
				}

				.alarm-list {
					overflow-y: scroll;
					font-family: 'Maersk Text';
				}

				.list-item {
					padding: 16px;
					border-bottom: 1px solid var(--functional-grey-400, grey);
				}

				.alarm-group-date {
					font-size: 16px;
					font-weight: bold;
					padding: 14px 16px 14px;
				}

				.alarm-header {
					font-size: 14px;
					display: flex;
					justify-content: space-between;
				}

				.header-seperator {
					font-weight: normal;
				}

				.alarm-type {
					font-weight: bold;
				}

				.alarm {
					font-size: 12px;
				}

				.reason {
					height: 15px;
				}
				.editButton {
					border: none;
					background: none;
					font-family: MaerskText;
					font-size: 14px;
					padding-right: 5px;
					padding-left: 5px;
					padding-bottom: 4px;
			   		color: var(--primary-blue-05, blue);
					text-decoration: underline;
					cursor: pointer;
                }
			`,
		];
	}

	render() {
		return this._loader ? html` <div class="list-item alarm"><mc-loading-indicator fit="small" label="Loading alarms"></mc-loading-indicator></div>` : Object.keys(this._groupedAlarms).length === 0  && !this._loader
			? html`<div>No alarms found</div>` 
			: html` ${this._renderAlarmsList()} `;
	}

	connectedCallback() {
		super.connectedCallback();

		VesselAlarmsModel!.addEventListener(AlarmEvents.AlarmsUpdated, this._refreshAlarms)
		this._refreshAlarms();
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		VesselAlarmsModel!.removeEventListener(AlarmEvents.AlarmsUpdated, this._refreshAlarms)
	}

	private _renderAlarmsList() {
		const dates = Object.keys(this._groupedAlarms).sort((a, b) => {
			const dateA = new Date(a);
			const dateB = new Date(b);
			return dateB.getTime() - dateA.getTime();
		});
		
		return html`
			<div class="alarm-list">
				${
					dates.map((date) => {
						return html`
							${this._renderAlarmGroup(date, this._groupedAlarms[date])}
						`;
					})
				}
			</div>
		`
	}

	private _renderAlarmGroup(date: string, alarms: VoyNotification<NotificationTypes.Alarm>[]) {
		const sortedAlarms = alarms.sort((a, b) => {
			const dateA = new Date(a.created_at);
			const dateB = new Date(b.created_at);
			return dateB.getTime() - dateA.getTime();
		})
		
		return html`
		${this._isAlarmDataValid(sortedAlarms)?
			html`
			<div class="list-item alarm-group-date">
				${this._formatDate(date, true, true, true, false, true)}
			</div>`:
			nothing
			}
			${sortedAlarms.map((alarm) => {
                if(isObjectEmpty(alarm.data)){
					return nothing;
				} else {
					return html` ${this._renderAlarm(alarm)} `;
				}
			})}
		`;
	}
	private _isAlarmDataValid(sortedAlarms:VoyNotification<NotificationTypes.Alarm>[]){
        return (sortedAlarms.length && !!sortedAlarms.filter(alarm => alarm.data && (Object.values(AlarmTypes).find((alarmtype:string) => alarmtype.includes(alarm.data?.alarmType?.toLocaleLowerCase())))))
	}
	private _renderAlarm(alarm: VoyNotification<NotificationTypes.Alarm>) {
		return html`
			<div class="list-item alarm">
				${this._renderAlarmHeader(alarm)}
				${this._renderAlarmState(alarm)}
				${this._renderReasonAndComment(alarm)}
			</div>
		`;
	}

	private _renderAlarmHeader(alarm: VoyNotification<NotificationTypes.Alarm>) {
		const title = (alarm.data.alarmType?.toLocaleLowerCase() == AlarmTypes.Crosstrack)? "offtrack": alarm.data.alarmType;
		return html`
			<div class="alarm-header">
				<div class="alarm-title">
					<span class="alarm-type">${formatToTitleCase(title? title : "")}</span>
					<span class="header-seperator"> | </span>
					<span class="alarm-title-date">${(this._formatDate((alarm.created_at), false, false, false, true, true)) + " UTC"}</span>
				</div>
				<!-- Showing edit button only when we have submitted the reason -->
				${ alarm.notification_responses.length>0  && !(isVesselOnCloud && alarm.state !=="Active") ? html`<button class="editButton" @click=${ () => { this._editAlarm(alarm.id)} }>Edit</button>`:nothing}
			
			</div>`
	}

	private _renderAlarmState(alarm: VoyNotification<NotificationTypes.Alarm>) {
		return html`
			<div>${formatToTitleCase(alarm.state)}</div>`
	}

	private _renderReasonAndComment(alarm: VoyNotification<NotificationTypes.Alarm>) {
		if (!alarm.notification_responses.length) {
			return;
		}

		const sortedAlarmResponses = alarm.notification_responses.sort((a, b) => {
			const dateA = new Date(a.created_at);
			const dateB = new Date(b.created_at);
			return dateB.getTime() - dateA.getTime();
		})

		const latestAlarmResponse = sortedAlarmResponses[0];
		const isEdited = sortedAlarmResponses.length > 1;
		return html`
			<div>
				<div class="reason">${"Reason " + this._formatDate(latestAlarmResponse.created_at, false, true, true, true, false) + this._renderEditedStatus(latestAlarmResponse,isEdited) + ":"}</div>
				${latestAlarmResponse.data.reason ? html`<div>${latestAlarmResponse.data.reason}</div>` : nothing}
				${latestAlarmResponse.data.comments ? html`<div>${this._renderComment(latestAlarmResponse)}</div>` : nothing}
			</div>
		`
	}

	private _renderEditedStatus(notificationResponse: NotificationResponse<NotificationTypes.Alarm>, isEdited: boolean) {
		if (!isEdited) {
			return "";
		}
		return " (edited)"
	}

	private _renderComment(notificationResponse: NotificationResponse<NotificationTypes.Alarm>) {
		return html`<div>
			<div class="comment">${"Comment: " + notificationResponse.data.comments}</div>
	</div>`
	}

	private _groupAlarmsByDate(
		alarms: VoyNotification<NotificationTypes.Alarm>[]
	): AlarmsGroupedByDate {
		const groupedAlarms: AlarmsGroupedByDate = {};

		for (const item of alarms) {
			const date = item.created_at.split("T")[0]; // Extracting the date part
			if (!(date in groupedAlarms)) {
				groupedAlarms[date] = [];
			}
			groupedAlarms[date].push(item);
		}
		return groupedAlarms;
	}
   private _editAlarm(id:string){
	const alarms = VesselAlarmsModel!.alarms;
	let alarm =  alarms.find((item) => item.id == id)
	if(alarm){
		VesselAlarmsModel!.editAlarms(alarm)
	}
   }
	async _refreshAlarms() {
		let alarms;
		if(isVesselOnCloud){
			this._loader = true
			alarms = await VesselNotificationsService.fetchNotifications(NotificationTypes.Alarm,"log");
		}
		else{
			 alarms = VesselAlarmsModel!.alarms;
		}

		this._groupedAlarms = alarms.length ? this._groupAlarmsByDate(alarms) : {};
		this._loader = false

	}

	private _formatDate(dateString: string, showYear: boolean, showMonth: boolean, showDay: boolean, showTime: boolean, longMonth: boolean) {
		const date = new Date(dateString);
		const day = date.getDate().toString().padStart(2, '0');
		const month = date.toLocaleString('default', { month: longMonth ? "long" : 'short' });
		const year = date.getFullYear();
		const hours = date.getHours().toString().padStart(2, '0');
		const minutes = date.getMinutes().toString().padStart(2, '0');
	  
		let formattedDate = "";
		if (showDay) {
			formattedDate = day;
		}
		if (showMonth) {
			formattedDate = formattedDate + " " + month;
		}
		if (showYear) {
			formattedDate = formattedDate + " " + year; 
		}
		if (showTime) {
			formattedDate = formattedDate + " " + hours + ":" + minutes;
		}
	  
		return formattedDate;
	}
}


